import { type ReactNode } from 'react';
import { tv } from 'tailwind-variants';

const root = tv({
  /* Spec vertical rhythm — Card→Card / stack bands (G2): --space-6, not grid gutter. */
  base: 'box-border flex min-w-0 max-w-full flex-col gap-[var(--space-6)]',
});

export interface ListPageLayoutProps {
  /** Optional hub, alert strip, or title adjunct above the KPI row. */
  readonly banner?: ReactNode;
  /**
   * Optional compact tools slot rendered between `banner` and `kpiRow`.
   *
   * Intended for the three mobile-management screens (Kiosky, Inventory, Incidenty)
   * on compact 320-767 layouts when tools need to appear above KPIs.
   * Leave `undefined` for the default expanded behavior where table tools stay in
   * `DataTableShell.toolbarCenter`.
   */
  readonly toolsBeforeKpi?: ReactNode;
  /** Optional KPI strip — typically a `KpiGrid` of `KpiCard` cells. */
  readonly kpiRow?: ReactNode;
  /**
   * Page-scoped secondary actions (canonical Obnovit via `ScreenContentActions`).
   * Renders after KPIs / before the table — never in `EnterprisePageHeader`.
   */
  readonly contentActions?: ReactNode;
  readonly children: ReactNode;
  /** Optional detail drawer rendered after main list content. */
  readonly drawer?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Canonical vertical stack for admin list screens under `EnterpriseShell` main.
 *
 * Ownership (all tabs):
 * - Page inset + main track width → `EnterpriseShell` `<main class="enterprise-shell-main">`
 *   only (fluid `max-width: var(--admin-content-max-width)` → `--container-full`; equal L/R from
 *   `--admin-content-inset-*`) — no per-screen pad / centered px content cap
 * - Spec 12/8/4 grid → `EnterpriseShell` `.admin-shell-grid` (`--shell-grid-columns`); this stack
 *   is a full-span child (do not re-mount display:grid here — vertical rhythm stays flex + `--space-6`)
 * - Page H1 (+ subtitle / `contextSlot`) → `EnterprisePageHeader` only
 * - This stack → optional `banner` → optional `toolsBeforeKpi` → `kpiRow` → optional
 *   `contentActions` (Obnovit SSOT) → table/`DataTableShell` → `drawer`
 * - Stack gap → `--space-6` (Spec Card→Card / list band rhythm; grid gutters stay on shell inset)
 * - Find/search tools default to `DataTableShell` `toolbarCenter`; compact tools-above-KPI
 *   is allowed only for Kiosky/Inventory/Incidenty when passed through `toolsBeforeKpi`
 * - Narrower containers: forms use `FormShell` (`--container-form`); reading prose → `--container-reading`
 *
 * Gold reference: `SalesPointsScreenView` / Products (no duplicate page title in the table).
 */
export function ListPageLayout({
  banner,
  toolsBeforeKpi,
  kpiRow,
  contentActions,
  children,
  drawer,
  className,
  testId = 'list-page-layout',
}: ListPageLayoutProps): JSX.Element {
  return (
    <div className={root({ className })} data-testid={testId}>
      {banner ? <div data-testid="list-page-layout-banner">{banner}</div> : null}
      {toolsBeforeKpi ? (
        <div data-testid="list-page-layout-tools-before-kpi">{toolsBeforeKpi}</div>
      ) : null}
      {kpiRow ? <div data-testid="list-page-layout-kpi">{kpiRow}</div> : null}
      {contentActions ? (
        <div data-testid="list-page-layout-content-actions">{contentActions}</div>
      ) : null}
      {children}
      {drawer ? <div data-testid="list-page-layout-drawer">{drawer}</div> : null}
    </div>
  );
}
